// =============================================================================
// eligibilityChatHistory.js
// -----------------------------------------------------------------------------
// Browser-local history helpers for the read-only Genie eligibility display.
// Only the variable eligibility snapshot is persisted; fixed guidance is
// reconstructed by the proposal page and no Firebase/backend writes are used.
// =============================================================================

// RECONSTRUCTED HISTORY: These messages are identical for every completed
// profile flow, so they never need to be stored with each proposal.
export const PROFILE_DETERMINISTIC_CHAT_MESSAGES = [
  "Please enter a Grant URL",
  "Please name your Proposal",
  "Optionally Upload or choose a previously uploaded file to improve results",
  "You can now start the Eligibility check!",
  "Please review and confirm your Company Info on the right, then click Confirm.",
  "Please review and confirm your Team Info on the right, then click Confirm.",
  "Please review and confirm the Grant Info on the right, then click Confirm.",
];

// STORAGE VERSION: Lets future changes safely distinguish older browser data.
const ELIGIBILITY_CHAT_STORAGE_VERSION = 1;
const ELIGIBILITY_CHAT_STORAGE_PREFIX = "grantProfileEligibility";

// STORAGE KEY: Scope the locally retained snapshot to its user and proposal.
export const getEligibilityChatStorageKey = ({ uid, clientId, grantId }) => {
  if (!uid || !clientId || !grantId) return null;

  return [
    ELIGIBILITY_CHAT_STORAGE_PREFIX,
    ELIGIBILITY_CHAT_STORAGE_VERSION,
    uid,
    clientId,
    grantId,
  ].join(":");
};

// SANITIZATION: Persist only values required by GeninieChatPanel. In particular,
// cached_content is intentionally excluded because it can be large and sensitive.
const sanitizeEligibilityResult = (result) => {
  if (!result || typeof result !== "object") return null;

  return {
    eligible: result.eligible,
    confidence: result.confidence || null,
    reasons: Array.isArray(result.reasons) ? result.reasons : [],
    uncertainties: Array.isArray(result.uncertainties) ? result.uncertainties : [],
    source_pages: Array.isArray(result.source_pages) ? result.source_pages : [],
  };
};

// SNAPSHOT CREATION: Keep only the variable portion of the eligibility flow.
// The deterministic Genie instructions are reconstructed on the destination page.
export const createEligibilityChatSnapshot = ({
  eligibilityResult,
  eligibilityError,
  clarifications,
  proposalPromptAnswer,
  grantUrl,
  hasConfirmedGrantInfo,
}) => ({
  version: ELIGIBILITY_CHAT_STORAGE_VERSION,
  savedAt: Date.now(),
  grantUrl: typeof grantUrl === "string" ? grantUrl : "",
  hasConfirmedGrantInfo: Boolean(hasConfirmedGrantInfo),
  eligibilityResult: sanitizeEligibilityResult(eligibilityResult),
  eligibilityError: typeof eligibilityError === "string" ? eligibilityError : null,
  clarifications:
    clarifications && typeof clarifications === "object" && !Array.isArray(clarifications)
      ? clarifications
      : {},
  proposalPromptAnswer:
    proposalPromptAnswer === "yes" || proposalPromptAnswer === "no"
      ? proposalPromptAnswer
      : null,
});

// LOCAL WRITE: Storage failures (quota/privacy mode) must not block navigation.
export const saveEligibilityChatSnapshot = (identifiers, snapshot) => {
  const storageKey = getEligibilityChatStorageKey(identifiers);
  if (!storageKey || typeof window === "undefined") return;

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(snapshot));
  } catch (error) {
    console.warn("Unable to save local eligibility history:", error);
  }
};

// LOCAL READ: Ignore malformed, old-schema, or unrelated browser records.
export const loadEligibilityChatSnapshot = (identifiers) => {
  const storageKey = getEligibilityChatStorageKey(identifiers);
  if (!storageKey || typeof window === "undefined") return null;

  try {
    const storedValue = window.localStorage.getItem(storageKey);
    if (!storedValue) return null;

    const snapshot = JSON.parse(storedValue);
    if (
      !snapshot ||
      typeof snapshot !== "object" ||
      snapshot.version !== ELIGIBILITY_CHAT_STORAGE_VERSION
    ) {
      return null;
    }

    return {
      ...snapshot,
      eligibilityResult: sanitizeEligibilityResult(snapshot.eligibilityResult),
      eligibilityError:
        typeof snapshot.eligibilityError === "string"
          ? snapshot.eligibilityError
          : null,
      clarifications:
        snapshot.clarifications &&
        typeof snapshot.clarifications === "object" &&
        !Array.isArray(snapshot.clarifications)
          ? snapshot.clarifications
          : {},
      proposalPromptAnswer:
        snapshot.proposalPromptAnswer === "yes" ||
        snapshot.proposalPromptAnswer === "no"
          ? snapshot.proposalPromptAnswer
          : null,
      grantUrl: typeof snapshot.grantUrl === "string" ? snapshot.grantUrl : "",
      hasConfirmedGrantInfo: Boolean(snapshot.hasConfirmedGrantInfo),
    };
  } catch (error) {
    console.warn("Unable to load local eligibility history:", error);
    return null;
  }
};